import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { ButtonProps } from '@mantine/core';
import { ColumnFiltersState, Table } from '@tanstack/react-table';
import { isValidDate } from 'rxjs/internal/util/isDate';
import { PredefinedIconSize } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types.ts';

export type TableStateExtended = {
	rowSelection?: Record<string, boolean>;
	pagination: { pageIndex: number; pageSize: number };
	sorting: Array<{ id: string; desc: boolean }>;
	columnFilters: ColumnFiltersState;
	globalFilter?: unknown;
	showColumnFilters?: boolean;
	tableAllSelected: boolean;
};

export type TableToolbarButtonProps<TData extends RecordAny> = ButtonProps & {
	table: Table<TData> & {
		options: { icons?: Record<string, React.FC>; localization?: Record<string, string> };
		setShowColumnFilters?: (v: boolean) => void;
		setIsFullScreen?: (v: boolean) => void;
	};
	title?: string;
	className?: string;
	iconSize?: PredefinedIconSize;
};

export type ColumnFilter = ColumnFiltersState[number];

export enum SelectionScope {
	All = 'All',
	Page = 'Page',
}

export interface MantineTableMeta {
	selectAll: boolean;
	setSelectAll: (value: boolean) => void;
	greedySelecting: boolean;
	setGreedySelecting: (v: boolean) => void;
	selectionScope: SelectionScope;
	setSelectionScope: (selection: SelectionScope | null) => void;
	columnWidths?: Record<string, number>;
}

const getTableSelectionCount = <T extends RecordAny>(table: Table<T>) =>
	getTableSelection(table)?.length ?? 0;

export const getTableSelection = <T extends RecordAny>(table: Table<T> | undefined) =>
	table?.getSelectedRowModel()?.rows ?? [];

export const getTableExcludedSelection = <T extends RecordAny>(
	table: Table<T> | undefined
): string[] => {
	const rowSelectionState = getTableSelectionState(table);
	return Object.keys(rowSelectionState).filter((rowId) => rowSelectionState[rowId] !== true);
};

export const getTableSelectedIds = <T extends RecordAny>(table: Table<T> | undefined): string[] => {
	const rowSelectionState = getTableSelectionState(table);
	return Object.keys(rowSelectionState).filter((rowId) => rowSelectionState[rowId] === true);
};

export const getTableSelectionState = <T extends RecordAny>(table: Table<T> | undefined) =>
	(table?.getState() as unknown as TableStateExtended)?.rowSelection ?? {};

export const getTableTotalCount = <T extends RecordAny>(table: Table<T>) => {
	const anyOptions = table.options as unknown as { rowCount?: number };
	return anyOptions.rowCount ?? table.getPrePaginationRowModel().rows.length;
};

export const getPageSize = <T extends RecordAny>(table: Table<T>) =>
	(table.getState() as unknown as TableStateExtended).pagination.pageSize;

export const getTableAllSelected = <T extends RecordAny>(table: Table<T>) => {
	return getFinalSelectionCount(table) === getTableTotalCount(table);
};

export const getFinalSelectionCount = <T extends RecordAny>(table: Table<T>) => {
	const rowSelectionState = getTableSelectionState(table);
	const selectedCount = Object.values(rowSelectionState).filter(Boolean).length;
	const { manualPagination, greedySelecting, selectionScope, selectAll } = getTableMeta(table);

	if (!manualPagination && selectAll && selectionScope === SelectionScope.All) {
		const excludedCount = getTableExcludedSelection(table).length;
		const totalCount = getTableTotalCount(table);
		return Math.max(0, totalCount - excludedCount);
	}

	if (manualPagination && greedySelecting) {
		const excludedCount = getTableSelectedIds(table).length;
		const totalCount = table.getRowCount();
		return Math.max(0, totalCount - excludedCount);
	}

	return selectedCount;
};

export const getTableAnySelected = <T extends RecordAny>(table: Table<T>) => {
	const rowSelectionState = getTableSelectionState(table);
	return Object.values(rowSelectionState).some(Boolean);
};

export const setTableAllSelected = <T extends RecordAny>(table: Table<T>, value: boolean) => {
	table.setState((state) => ({ ...state, tableAllSelected: value }));
	table.toggleAllRowsSelected(true);
};

export const setAllRowsSelected = <T extends RecordAny>(table: Table<T>, value: boolean) => {
	const { manualPagination, greedySelecting } = getTableMeta(table);

	if (manualPagination && greedySelecting) {
		// when manually paginating & greedy selecting, we do not have all rows locally
		// thus we can't set _all_ rows to the selected state
		// so we always clear the selection state to avoid selection confusion
		table.setRowSelection({});
		return;
	} else if (manualPagination) {
		const rowIds = table
			.getRowModel()
			.rows.filter((row) => row.getCanSelect())
			.map((row) => row.id);
		const selectionState = table.getState().rowSelection;
		const allSelectedState = rowIds.reduce(
			(acc, id) => ({ ...acc, [id]: value }),
			selectionState
		);
		table.setRowSelection(allSelectedState);
		return;
	}

	const allRowIds = table
		.getPrePaginationRowModel()
		.rows.filter((row) => row.getCanSelect())
		.map((row) => row.id);
	const allSelectedState = allRowIds.reduce((acc, id) => ({ ...acc, [id]: value }), {});
	table.setRowSelection(allSelectedState);
};

export const getTableTotalorSelectedCount = <T extends RecordAny>(table: Table<T>) =>
	getTableSelectionCount(table) || getTableTotalCount(table);

// date filters are serialized as strings and need to be converted back to Date objects
export const convertDateRangeFiltersFromStringsToDates = (filters: ColumnFiltersState) => {
	// add date field ids as the structure is breaking other similar fields and setting them to null
	const dateFieldIds = ['date', 'updatedAt', 'createdAt', 'dueDate', 'startDate', 'endDate'];

	return filters.map((filter) => {
		// Only process date conversion if the filter is for a date field
		if (dateFieldIds.includes(filter.id)) {
			return {
				...filter,
				value: Array.isArray(filter.value)
					? filter.value.map((item) => {
							let dateVal = item;
							if (typeof item === 'string') {
								dateVal = new Date(item);
							}
							if (!isValidDate(dateVal)) {
								dateVal = null;
							}
							return dateVal;
						})
					: filter.value,
			};
		}
		// Return non-date filters unchanged
		return filter;
	});
};

/**
 * Total pages for a result set, floored at 1 so the pager always has a page to render.
 * Unusable page sizes (0, negative, NaN from a malformed url param) collapse to 1.
 */
export const getTotalPages = ({
	rowCount,
	pageSize,
}: {
	rowCount: number;
	pageSize: number;
}): number => {
	if (!Number.isFinite(pageSize) || pageSize <= 0 || !Number.isFinite(rowCount)) {
		return 1;
	}
	return Math.max(1, Math.ceil(rowCount / pageSize));
};

/**
 * Caps to the last page with rows; prevents server skip-past-end. Non-finite indices
 * never compare equal to self, so the caller would correct it forever without this guard.
 */
export const getClampedPageIndex = ({
	rowCount,
	pageIndex,
	pageSize,
}: {
	rowCount: number;
	pageIndex: number;
	pageSize: number;
}): number => {
	if (!Number.isFinite(pageIndex)) {
		return 0;
	}
	return Math.trunc(Math.min(Math.max(0, pageIndex), getTotalPages({ rowCount, pageSize }) - 1));
};

export const setAllVisibleRowsSelected = <T extends RecordAny>(table: Table<T>, value: boolean) => {
	const currentState = table.getState().rowSelection;
	const visibleRowIds = table
		.getPaginationRowModel()
		.rows.filter((row) => row.getCanSelect())
		.map((row) => row.id);
	const newState = visibleRowIds.reduce(
		(acc, rowId) => ({
			...acc,
			[rowId]: value,
		}),
		{
			...currentState,
		}
	);

	table.setRowSelection(newState);
};

export const toIdListFilterValue = (filterValue: unknown): string[] =>
	(Array.isArray(filterValue) ? filterValue : [filterValue])
		.filter((value): value is string => typeof value === 'string' && !!value.trim())
		.map((value) => value.trim());

export const emptyToUndefined = (ids: string[]): string[] | undefined =>
	ids.length ? ids : undefined;

interface FilterableRow {
	getValue: (columnId: string) => unknown;
}

export const idListFilterFn = Object.assign(
	(row: FilterableRow, columnId: string, ids: string[]): boolean =>
		!ids.length || ids.includes(String(row.getValue(columnId))),
	{ resolveFilterValue: toIdListFilterValue }
);
